import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';
import en from './en';

type TranslationMap = Record<string, string>;

const SUPPORTED_LOCALES = ['en', 'hu', 'de', 'es', 'fr', 'it', 'pt'] as const;
export type Locale = typeof SUPPORTED_LOCALES[number];

// Map Triton codes to our locale codes
const TRITON_LOCALE_MAP: Record<string, Locale> = {
  en_GB: 'en',
  'hu-HU': 'hu',
  de_DE: 'de',
  es_ES: 'es',
  fr_FR: 'fr',
  it_IT: 'it',
  pt_BR: 'pt',
};

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  hu: 'Magyar',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  pt: 'Português',
};

export const LOCALE_FLAGS: Record<Locale, string> = {
  en: '🇬🇧',
  hu: '🇭🇺',
  de: '🇩🇪',
  es: '🇪🇸',
  fr: '🇫🇷',
  it: '🇮🇹',
  pt: '🇧🇷',
};

// Lazy load translations
const translationLoaders: Record<Locale, () => Promise<{ default: TranslationMap }>> = {
  en: () => Promise.resolve({ default: en }),
  hu: () => import('./hu'),
  de: () => import('./de'),
  es: () => import('./es'),
  fr: () => import('./fr'),
  it: () => import('./it'),
  pt: () => import('./pt'),
};

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const useTranslation = (): I18nContextType => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within I18nProvider');
  return ctx;
};

const STORAGE_KEY = 'silverclaw_locale';

function getInitialLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) return stored as Locale;
  return 'en';
}

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);
  const [translations, setTranslations] = useState<TranslationMap>(en);
  const { user, isAuthenticated } = useAuth();

  // Load translations when locale changes
  useEffect(() => {
    let cancelled = false;
    translationLoaders[locale]().then((mod) => {
      if (!cancelled) setTranslations(mod.default);
    });
    return () => { cancelled = true; };
  }, [locale]);

  // Fetch Triton language when user logs in
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const fetchTritonLang = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const res = await fetch(`${API_BASE}/users/language`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.locale) {
            const mapped = TRITON_LOCALE_MAP[data.locale] || 'en';
            setLocaleState(mapped);
            localStorage.setItem(STORAGE_KEY, mapped);
          }
        }
      } catch {
        // silently ignore – keep current locale
      }
    };
    fetchTritonLang();
  }, [isAuthenticated, user]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem(STORAGE_KEY, l);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      let text = translations[key] ?? en[key] ?? key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        });
      }
      return text;
    },
    [translations],
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
};
